import type { ReactNode } from "react";
import type { Dashboard, DashboardSeriesPoint } from "@/types";

import { TrendChip } from "@heroui-pro/react";
import { KPI } from "@heroui-pro/react/kpi";
import { ArrowDown, ArrowUp } from "lucide-react";

import { aedRounded } from "@/components/sales/shared";

type Kind = "aed" | "count" | "percent";

const FORMAT = {
  aed: { style: "currency", currency: "AED", maximumFractionDigits: 0 },
  count: { maximumFractionDigits: 0 },
  percent: { style: "percent", maximumFractionDigits: 1 },
} as const;

export const ratio = (part: number, whole: number) =>
  whole ? part / whole : 0;

/** Rates change in percentage points, everything else in percent of the previous value. */
function Trend({
  now,
  before,
  kind,
  lowerIsBetter,
}: {
  now: number;
  before: number;
  kind: Kind;
  lowerIsBetter?: boolean;
}) {
  const delta =
    kind === "percent"
      ? (now - before) * 100
      : before
        ? ((now - before) / Math.abs(before)) * 100
        : null;

  if (delta === null) return null;

  const direction =
    Math.abs(delta) < 0.05 ? "neutral" : delta > 0 ? "up" : "down";
  // The chip's colour says good or bad; the arrow keeps the real direction.
  const verdict =
    direction === "neutral"
      ? "neutral"
      : (direction === "up") !== Boolean(lowerIsBetter)
        ? "up"
        : "down";
  const Arrow = direction === "up" ? ArrowUp : ArrowDown;

  return (
    <KPI.Trend trend={verdict}>
      {direction !== verdict && direction !== "neutral" ? (
        <TrendChip.Indicator>
          <Arrow />
        </TrendChip.Indicator>
      ) : null}
      {`${Math.abs(delta).toFixed(1)}${kind === "percent" ? " pt" : "%"}`}
    </KPI.Trend>
  );
}

function Tile({
  title,
  kind,
  now,
  before,
  lowerIsBetter,
  footer,
  spark,
}: {
  title: string;
  kind: Kind;
  now: number;
  before: number;
  lowerIsBetter?: boolean;
  footer?: ReactNode;
  spark?: { date: string; value: number }[];
}) {
  return (
    <KPI className="min-w-0">
      <KPI.Header>
        <KPI.Title>{title}</KPI.Title>
      </KPI.Header>
      {/* flex-none keeps the value at the top when a tile has no sparkline */}
      <KPI.Content className="flex-none">
        <KPI.Value className="tabular-nums" value={now} {...FORMAT[kind]} />
        <Trend
          before={before}
          kind={kind}
          lowerIsBetter={lowerIsBetter}
          now={now}
        />
      </KPI.Content>
      {footer ? (
        <KPI.Footer className="text-muted text-xs">{footer}</KPI.Footer>
      ) : null}
      {spark && spark.length > 1 ? (
        <KPI.Chart color="var(--chart-1)" data={spark} height={48} />
      ) : null}
    </KPI>
  );
}

const sparkOf = (
  series: DashboardSeriesPoint[],
  value: (point: DashboardSeriesPoint) => number,
) => series.map((point) => ({ date: point.date, value: value(point) }));

const count = (n: number) => n.toLocaleString();

export function DashboardKpis({ data }: { data: Dashboard }) {
  const { current: c, previous: p, series } = data;
  const margin = (d: typeof c) => ratio(d.grossProfit, d.costedRevenue);
  const oosRate = (d: typeof c) => ratio(d.oosUnits, d.fulfilledUnits);
  const refunds = (d: typeof c) =>
    d.refundsCancellation + d.refundsPackaging + d.refundsManual;
  const costed = ratio(c.costedOrders, c.fulfilledOrders);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        <Tile
          before={p.netRevenue}
          footer={
            data.itemFiltered
              ? "Product lines only, no fees or order discounts"
              : `${aedRounded(c.fees)} fees · ${count(c.openOrders)} orders still open`
          }
          kind="aed"
          now={c.netRevenue}
          spark={sparkOf(series, (s) => s.netRevenue)}
          title="Net revenue"
        />
        <Tile
          before={p.grossProfit}
          footer={
            <span className={costed < 0.95 ? "text-warning" : undefined}>
              {`${(costed * 100).toFixed(0)}% of fulfilled orders have every buy price`}
            </span>
          }
          kind="aed"
          now={c.grossProfit}
          spark={sparkOf(series, (s) => s.grossProfit)}
          title="Gross profit"
        />
        <Tile
          before={margin(p)}
          footer={`On ${aedRounded(c.costedRevenue)} of costed revenue`}
          kind="percent"
          now={margin(c)}
          title="Gross margin"
        />
        <Tile
          before={p.orders}
          footer={`${count(c.checkouts)} checkouts`}
          kind="count"
          now={c.orders}
          spark={sparkOf(
            series,
            (s) => s.openOrders + s.fulfilledOrders + s.cancelledOrders,
          )}
          title="Orders placed"
        />
        <Tile
          before={p.units}
          footer={`${count(c.specialUnits)} from order anything`}
          kind="count"
          now={c.units}
          spark={sparkOf(series, (s) => s.units)}
          title="Units ordered"
        />
        <Tile
          lowerIsBetter
          before={oosRate(p)}
          footer={`${count(c.oosUnits)} of ${count(c.fulfilledUnits)} units`}
          kind="percent"
          now={oosRate(c)}
          spark={sparkOf(series, (s) => ratio(s.oosUnits, s.fulfilledUnits))}
          title="Out-of-stock rate"
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        <Tile
          before={ratio(p.netRevenue, p.fulfilledOrders)}
          footer={`Over ${count(c.fulfilledOrders)} fulfilled orders`}
          kind="aed"
          now={ratio(c.netRevenue, c.fulfilledOrders)}
          title="Average order"
        />
        <Tile
          before={p.openOrders}
          footer={`${aedRounded(c.openValue)} expected`}
          kind="count"
          now={c.openOrders}
          title="Open orders"
        />
        <Tile
          lowerIsBetter
          before={ratio(p.cancelledOrders, p.orders)}
          footer={`${count(c.cancelledOrders)} cancelled`}
          kind="percent"
          now={ratio(c.cancelledOrders, c.orders)}
          title="Cancellation rate"
        />
        <Tile
          lowerIsBetter
          before={ratio(p.similarUnits, p.boughtUnits)}
          footer={`${count(c.similarUnits)} of ${count(c.boughtUnits)} bought units`}
          kind="percent"
          now={ratio(c.similarUnits, c.boughtUnits)}
          title="Substitution rate"
        />
        <Tile
          before={p.customers}
          footer={`${count(c.newOrders)} new · ${count(c.orders - c.newOrders)} returning orders`}
          kind="count"
          now={c.customers}
          title="Active customers"
        />
        <Tile
          lowerIsBetter
          before={refunds(p)}
          footer={`Cancel ${aedRounded(c.refundsCancellation)} · packing ${aedRounded(c.refundsPackaging)} · manual ${aedRounded(c.refundsManual)}`}
          kind="aed"
          now={refunds(c)}
          title="Refunds"
        />
      </div>
    </div>
  );
}
